import { Storage, Auth } from 'aws-amplify';

/**
 * Upload an image file to S3 using AWS Amplify Storage
 * @param {File|Blob} file - The file or blob to upload
 * @param {string} prefix - Optional prefix for the filename
 * @returns {Promise<string>} - Returns the public S3 URL of the uploaded file
 */
export const uploadImageToS3 = async (file, prefix = 'demo') => {
  try {
    // Generate unique filename
    const timestamp = Date.now();
    const randomString = Math.random()
      .toString(36)
      .substring(2, 15);
    const extension = file.name ? file.name.split('.').pop() : 'jpg';
    const filename = `${prefix}_${timestamp}_${randomString}.${extension}`;

    // Upload to S3 with public access
    const result = await Storage.put(filename, file, {
      level: 'public',
      contentType: file.type || 'image/jpeg',
      metadata: {
        uploadedAt: new Date().toISOString(),
      },
    });

    // Get the public URL
    const url = await Storage.get(result.key, { level: 'public' });

    // Return the full S3 URL (remove query parameters if any)
    const fullUrl = url.split('?')[0];

    return fullUrl;
  } catch (error) {
    console.error('S3 upload error:', error);
    throw new Error(`Failed to upload image: ${error.message}`);
  }
};

/**
 * Upload user photo to S3 with organized folder structure for Virtual Try-On
 * Folder structure: virtual-try-on/{photoType}/{filename}
 * @param {File|Blob} file - The file or blob to upload
 * @param {string} photoType - Photo type (e.g., 'full-body-front', 'head-only')
 * @returns {Promise<object>} - Returns photo metadata including S3 path and URL
 */
export const uploadUserPhotoToS3 = async (file, photoType = 'full-body-front') => {
  try {
    // Get current user credentials to construct full S3 path
    const credentials = await Auth.currentCredentials();
    const identityId = credentials.identityId;

    // Generate unique filename
    const timestamp = Date.now();
    const randomString = Math.random()
      .toString(36)
      .substring(2, 15);
    const extension = file.name ? file.name.split('.').pop() : 'jpg';
    const filename = `${timestamp}_${randomString}.${extension}`;

    // Create organized path: virtual-try-on/{photoType}/{filename}
    const s3Key = `virtual-try-on/${photoType}/${filename}`;

    // Upload to S3 with protected access (user-specific)
    const result = await Storage.put(s3Key, file, {
      level: 'protected',
      contentType: file.type || 'image/jpeg',
    });

    // Get the protected URL
    const url = await Storage.get(result.key, { level: 'protected' });

    // Backend expects the FULL S3 key including protected prefix
    // AWS Amplify stores protected files at: protected/{identityId}/{key}
    const photoPath = `protected/${identityId}/${result.key}`;

    // Return photo metadata
    const photoData = {
      id: `${timestamp}_${randomString}`,
      s3Key: result.key,
      photoPath: photoPath, // Backend expects FULL S3 key
      url: url,
      photoType: photoType,
      uploadedAt: new Date().toISOString(),
    };

    return photoData;
  } catch (error) {
    console.error('[S3] User photo upload error:', error);
    throw new Error(`Failed to upload user photo: ${error.message}`);
  }
};

/**
 * Upload image from base64 data URL
 * @param {string} dataUrl - Base64 data URL
 * @param {string} prefix - Optional prefix for the filename
 * @returns {Promise<string>} - Returns the public S3 URL
 */
export const uploadBase64ToS3 = async (dataUrl, prefix = 'demo') => {
  try {
    // Convert base64 to blob
    const blob = await fetch(dataUrl).then(res => res.blob());
    return await uploadImageToS3(blob, prefix);
  } catch (error) {
    console.error('Base64 upload error:', error);
    throw new Error(`Failed to upload base64 image: ${error.message}`);
  }
};

/**
 * Delete an image from S3
 * @param {string} key - The S3 key of the file to delete
 * @returns {Promise<void>}
 */
export const deleteImageFromS3 = async key => {
  try {
    await Storage.remove(key, { level: 'public' });
  } catch (error) {
    console.error('S3 delete error:', error);
    throw new Error(`Failed to delete image: ${error.message}`);
  }
};

/**
 * Delete user photo from S3
 * Note: Only deletes user-uploaded photos, not generated try-on results
 * @param {string} key - The S3 key of the file to delete
 * @returns {Promise<void>}
 */
export const deleteUserPhotoFromS3 = async key => {
  try {
    // Safety check: Don't allow deletion of generated results
    if (key.includes('/results/')) {
      throw new Error('Cannot delete generated try-on results. Please use the backend API to delete results.');
    }

    await Storage.remove(key, { level: 'protected' });
  } catch (error) {
    console.error('User photo S3 delete error:', error);
    throw new Error(`Failed to delete user photo: ${error.message}`);
  }
};

/**
 * List all user photos from S3
 * @returns {Promise<Array>} - Returns array of user photo objects
 */
export const listUserPhotosFromS3 = async () => {
  try {
    // Get current user credentials to construct full S3 path
    const credentials = await Auth.currentCredentials();
    const identityId = credentials.identityId;

    // List all files in user's virtual-try-on folder
    const result = await Storage.list('virtual-try-on/', {
      level: 'protected',
    });

    // Storage.list returns an object with 'results' property (array of files)
    // Handle both old and new Amplify API formats
    const files = result.results || result || [];

    // Check if files is actually an array
    if (!Array.isArray(files)) {
      return [];
    }

    // Filter out directories (they end with /) and generated results
    const photoFiles = files.filter(item => {
      if (!item.key || item.key.endsWith('/')) return false;

      // Exclude generated try-on results (in 'results' folder)
      // Only include user-uploaded photos (in photo type folders like 'full-body-front')
      if (item.key.includes('/results/')) return false;

      return true;
    });

    if (photoFiles.length === 0) {
      return [];
    }

    // Process and return photo metadata
    const photos = await Promise.all(
      photoFiles.map(async item => {
        // Extract photo type from path: virtual-try-on/{photoType}/{filename}
        const pathParts = item.key.split('/');
        const photoType = pathParts[1] || 'full-body-front';
        const filename = pathParts[2] || '';

        // Get the URL
        const url = await Storage.get(item.key, { level: 'protected' });

        // Extract ID from filename (timestamp_random)
        const id = filename.replace(/\.[^/.]+$/, ''); // Remove extension

        // Backend expects the FULL S3 key including protected prefix
        // AWS Amplify stores protected files at: protected/{identityId}/{key}
        const photoPath = `protected/${identityId}/${item.key}`;

        return {
          id: id,
          s3Key: item.key,
          photoPath: photoPath, // Backend expects FULL S3 key with protected prefix
          url: url,
          photoType: photoType,
          uploadedAt: item.lastModified ? item.lastModified.toISOString() : new Date().toISOString(),
          size: item.size,
        };
      }),
    );

    const validPhotos = photos.filter(photo => photo.id);

    return validPhotos;
  } catch (error) {
    console.error('[S3] List user photos error:', error);
    throw new Error(`Failed to list user photos: ${error.message}`);
  }
};
